/**
 * Forward-reuse centrality from the generated learning script (the player-side
 * adapter over `@ssi/core`'s `computeLegoCentrality`).
 *
 * The full-course script walk (`generateLearningScript`) already carries
 * everything the distinction-network criticality ruling (Tom, 2026-07-31 —
 * see `centrality.ts` and docs/DECISIONS.md) needs: every LEGO's debut in
 * introduction order and every content-level BUILD/USE phrase with its owning
 * LEGO. So the centrality map is computed here once per script generation —
 * no new fetch, no new tables, one indexed in-memory pass.
 *
 * What counts as a "subsequent phrase": BUILD and USE items only. spaced_rep
 * items are REPLAYS of earlier USE content at later round positions — counting
 * them would measure the review schedule, not the network. Intro/component/
 * listening/pod items carry no production-phrase content.
 */

import {
  computeLegoCentrality,
  type CentralityLego,
  type CentralityPhrase,
  type LegoCentrality,
  type CourseBundle,
} from '@ssi/core'
import type { ScriptItem } from '../providers/generateLearningScript'

export interface ScriptCentrality {
  /** legoId → percentile (0..1, 1 = biggest hub) — the shape `planRound` consumes. */
  percentileByLego: Record<string, number>
  /** Full per-lego detail (raw forward-reuse counts) for logs and sanity reports. */
  detail: Map<string, LegoCentrality>
}

export function computeCentralityFromScript(items: ScriptItem[]): ScriptCentrality {
  const legos: CentralityLego[] = []
  const ordinalByLego = new Map<string, number>()

  for (const item of items) {
    if (item.type !== 'debut' || !item.legoKey) continue
    if (ordinalByLego.has(item.legoKey)) continue // spaced-rep/revival re-emissions of a debut
    const ordinal = ordinalByLego.size + 1
    ordinalByLego.set(item.legoKey, ordinal)
    legos.push({ legoId: item.legoKey, ordinal, target: item.targetText })
  }

  const phrases: CentralityPhrase[] = []
  // The INF-PLAY revival tail re-emits USE items — same content, later round
  // position. Centrality counts distinct CONTENT phrases, so dedupe on
  // (owner, target); replays measure the schedule, not the network.
  const seenPhrases = new Set<string>()
  for (const item of items) {
    if (item.type !== 'build' && item.type !== 'use') continue
    const ownerOrdinal = ordinalByLego.get(item.legoKey)
    if (ownerOrdinal === undefined) continue
    const key = `${item.legoKey} ${item.targetText}`
    if (seenPhrases.has(key)) continue
    seenPhrases.add(key)
    phrases.push({ ownerOrdinal, target: item.targetText })
  }

  const detail = computeLegoCentrality(legos, phrases)
  const percentileByLego: Record<string, number> = {}
  for (const [legoId, c] of detail) percentileByLego[legoId] = c.percentile
  return { percentileByLego, detail }
}

/**
 * The same map, straight from the bundle (bundle-cutover step 6).
 *
 * When a flagged course stops walking Supabase there is no `ScriptItem[]` to
 * read, and the bundle carries the two inputs more directly than the script
 * ever did: LEGOs in introduction order (the round map) and every BUILD/USE
 * phrase with its owning LEGO. Phrases missing any of their three clips are
 * excluded, because that is exactly what the walk's script did not contain —
 * an unplayable phrase is not a phrase the learner can be blocked by.
 */
export function computeCentralityFromBundle(bundle: CourseBundle): ScriptCentrality {
  const legos: CentralityLego[] = []
  const ordinalByLego = new Map<string, number>()
  const targetByLego = new Map(bundle.legos.map((l) => [l.legoId, l.targetText]))

  for (const entry of bundle.roundMap) {
    if (ordinalByLego.has(entry.legoId)) continue
    const ordinal = ordinalByLego.size + 1
    ordinalByLego.set(entry.legoId, ordinal)
    legos.push({ legoId: entry.legoId, ordinal, target: targetByLego.get(entry.legoId) ?? '' })
  }

  const phrases: CentralityPhrase[] = []
  const seenPhrases = new Set<string>()
  for (const p of bundle.phrases) {
    if (p.role !== 'build' && p.role !== 'use') continue
    if (!p.audio?.known || !p.audio?.target1 || !p.audio?.target2) continue
    const ownerOrdinal = ordinalByLego.get(p.legoId)
    if (ownerOrdinal === undefined) continue
    const key = `${p.legoId} ${p.targetText}`
    if (seenPhrases.has(key)) continue
    seenPhrases.add(key)
    phrases.push({ ownerOrdinal, target: p.targetText })
  }

  const detail = computeLegoCentrality(legos, phrases)
  const percentileByLego: Record<string, number> = {}
  for (const [legoId, c] of detail) percentileByLego[legoId] = c.percentile
  return { percentileByLego, detail }
}
